import airportCountryByIata from '../data/airportCountryByIata.json';

/** US state / territory codes (2-letter). */
const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA',
  'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT',
  'VA', 'WA', 'WV', 'WI', 'WY', 'DC', 'PR', 'VI', 'GU', 'AS', 'MP'
]);

/**
 * Codes that are both US states and ISO 3166-1 countries — never map these to US from state alone.
 * (e.g. IN = Indiana vs India, GA = Georgia state vs Georgia country)
 */
const US_STATE_ISO_COUNTRY_OVERLAP = new Set([
  'AL', 'AR', 'CA', 'CO', 'DE', 'GA', 'ID', 'IN', 'LA', 'MA', 'MD', 'ME', 'MT', 'NE', 'PA', 'SC', 'VA'
]);

/** API / dataset aliases → flag-icons ISO 3166-1 alpha-2 filenames. */
const COUNTRY_ALIASES = {
  UK: 'GB'
};

const FLAG_BASE_PATH = (process.env.BASE_URL || '/').replace(/\/?$/, '/');

/**
 * Resolves a 2-letter region code from flight data to an ISO 3166-1 alpha-2 country code.
 * @param {string} regionCode - airportState / state from API (US state or country)
 * @returns {string|null}
 */
export function resolveCountryCode(regionCode) {
  if (!regionCode || typeof regionCode !== 'string') return null;
  const code = regionCode.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code)) return null;
  if (US_STATE_ISO_COUNTRY_OVERLAP.has(code)) {
    return COUNTRY_ALIASES[code] || code;
  }
  if (US_STATE_CODES.has(code)) return 'US';
  return COUNTRY_ALIASES[code] || code;
}

/**
 * Looks up country code by IATA airport code (e.g. FRA → DE, DEL → IN).
 * @param {string} iataCode
 * @returns {string|null}
 */
export function countryCodeFromAirportIata(iataCode) {
  if (!iataCode || typeof iataCode !== 'string') return null;
  const iata = iataCode.trim().toUpperCase();
  if (iata.length !== 3) return null;
  const country = airportCountryByIata[iata];
  return country ? (COUNTRY_ALIASES[country] || country) : null;
}

/**
 * Resolves country for a flight: airport IATA first, then state/region.
 * @param {object} flight
 * @returns {string|null}
 */
export function resolveFlightCountryCode(flight) {
  if (!flight) return null;
  return (
    countryCodeFromAirportIata(flight.airportCode || flight.cityCode)
    || resolveCountryCode(flight.airportState || flight.state)
  );
}

/**
 * Public URL for a 4x3 SVG country flag (files in public/flags/4x3).
 * @param {string} countryCode - ISO 3166-1 alpha-2
 * @returns {string|null}
 */
export function getCountryFlagUrl(countryCode) {
  if (!countryCode) return null;
  const normalized = COUNTRY_ALIASES[countryCode] || countryCode;
  return `${FLAG_BASE_PATH}flags/4x3/${normalized.toLowerCase()}.svg`;
}

/**
 * Flag image URL for a flight row.
 * @param {object} flight
 * @returns {string|null}
 */
export function getFlightCountryFlagUrl(flight) {
  return getCountryFlagUrl(resolveFlightCountryCode(flight));
}
